import axios, { AxiosError, AxiosResponse } from "axios";
import {
  call,
  cancel,
  delay,
  fork,
  put,
  select,
  take,
  takeLatest,
} from "@redux-saga/core/effects";
import { Task } from "@redux-saga/types";

import { RootState } from "data-handler/rootReducer";

const prefix = "schoolconnect/batchHeal";

export const HEAL_TRIGGER_REQUEST = `${prefix}/TRIGGER_REQUEST`;
export const HEAL_TRIGGER_SUCCESS = `${prefix}/TRIGGER_SUCCESS`;
export const HEAL_TRIGGER_FAILURE = `${prefix}/TRIGGER_FAILURE`;

export const HEAL_POLL_UPDATE = `${prefix}/POLL_UPDATE`;
export const HEAL_POLL_STOP = `${prefix}/POLL_STOP`;

export const HEAL_CANCEL_REQUEST = `${prefix}/CANCEL_REQUEST`;
export const HEAL_CANCEL_SUCCESS = `${prefix}/CANCEL_SUCCESS`;
export const HEAL_CANCEL_FAILURE = `${prefix}/CANCEL_FAILURE`;

export const HEAL_RESET = `${prefix}/RESET`;

export type HealBatchProgress = {
  completed_schools: number;
  total_schools: number;
  completed_stocks: number;
  total_stocks: number;
};

export type BatchHealState = {
  batchId: string | null;
  status:
    | "idle"
    | "triggering"
    | "processing"
    | "completed"
    | "cancelled"
    | "error";
  progress: HealBatchProgress | null;
  error: string | null;
};

type TriggerRequestAction = {
  type: typeof HEAL_TRIGGER_REQUEST;
  filters: Record<string, string | number>;
};

type TriggerSuccessAction = {
  type: typeof HEAL_TRIGGER_SUCCESS;
  batchId: string;
};

type TriggerFailureAction = {
  type: typeof HEAL_TRIGGER_FAILURE;
  error: string;
};

type PollUpdateAction = {
  type: typeof HEAL_POLL_UPDATE;
  status: string;
  progress: HealBatchProgress;
};

type PollStopAction = {
  type: typeof HEAL_POLL_STOP;
  status: string;
};

type CancelSuccessAction = {
  type: typeof HEAL_CANCEL_SUCCESS;
};

type CancelFailureAction = {
  type: typeof HEAL_CANCEL_FAILURE;
  error: string;
};

type ResetAction = {
  type: typeof HEAL_RESET;
};

type Action =
  | TriggerRequestAction
  | TriggerSuccessAction
  | TriggerFailureAction
  | PollUpdateAction
  | PollStopAction
  | { type: typeof HEAL_CANCEL_REQUEST }
  | CancelSuccessAction
  | CancelFailureAction
  | ResetAction;

const initialState: BatchHealState = {
  batchId: null,
  status: "idle",
  progress: null,
  error: null,
};

const reducer = (
  state: BatchHealState = initialState,
  action: Action
): BatchHealState => {
  switch (action.type) {
    case HEAL_TRIGGER_REQUEST:
      return { ...initialState, status: "triggering" };
    case HEAL_TRIGGER_SUCCESS:
      return {
        ...state,
        batchId: (action as TriggerSuccessAction).batchId,
        status: "processing",
      };
    case HEAL_TRIGGER_FAILURE:
      return {
        ...state,
        status: "error",
        error: (action as TriggerFailureAction).error,
      };
    case HEAL_POLL_UPDATE: {
      const a = action as PollUpdateAction;
      const resolved =
        a.status === "completed"
          ? "completed"
          : a.status === "cancelled"
          ? "cancelled"
          : "processing";
      return {
        ...state,
        status: resolved,
        progress: a.progress,
      };
    }
    case HEAL_POLL_STOP: {
      const a = action as PollStopAction;
      return {
        ...state,
        status: a.status === "cancelled" ? "cancelled" : state.status,
      };
    }
    case HEAL_CANCEL_SUCCESS:
      return { ...state, status: "cancelled" };
    case HEAL_CANCEL_FAILURE:
      return {
        ...state,
        error: (action as CancelFailureAction).error,
      };
    case HEAL_RESET:
      return initialState;
    default:
      return state;
  }
};

export default reducer;

const baseUrl = () => (process.env.REACT_APP_API_URL ?? "").replace(/\/$/, "");

function triggerHealApi(filters: Record<string, string | number>) {
  return axios.post(`${baseUrl()}/store_manager/store-doctor/heal/`, filters);
}

function pollHealStatusApi(batchId: string) {
  return axios.get(`${baseUrl()}/store_manager/store-doctor/heal/${batchId}/`);
}

function cancelHealApi(batchId: string) {
  return axios.delete(
    `${baseUrl()}/store_manager/store-doctor/heal/${batchId}/`
  );
}

function buildProgressFromResponse(data: any): HealBatchProgress {
  if (data.progress) {
    return {
      completed_schools: data.progress.completed_schools ?? 0,
      total_schools: data.progress.total_schools ?? 0,
      completed_stocks: data.progress.completed_stocks ?? 0,
      total_stocks: data.progress.total_stocks ?? 0,
    };
  }

  const chunks = data.chunks ?? [];
  let completedSchools = 0;
  let totalSchools = data.total_schools ?? 0;
  let completedStocks = data.completed_stocks ?? 0;
  let totalStocks = data.total_stocks ?? 0;

  chunks.forEach((c: any) => {
    if (c.status === "completed") {
      completedSchools += (c.school_ids ?? []).length;
    } else if (c.status === "processing" && c.progress) {
      completedSchools += c.progress.current ?? 0;
    }
  });

  return {
    completed_schools: completedSchools,
    total_schools: totalSchools,
    completed_stocks: completedStocks,
    total_stocks: totalStocks,
  };
}

function* pollLoop(batchId: string) {
  while (true) {
    try {
      const resp: AxiosResponse = yield call(pollHealStatusApi, batchId);
      const data = resp.data;
      const status = data.status as string;
      const progress = buildProgressFromResponse(data);

      const allDone =
        status === "processing" &&
        progress.total_stocks > 0 &&
        progress.completed_stocks >= progress.total_stocks;
      const resolvedStatus = allDone ? "completed" : status;

      yield put({ type: HEAL_POLL_UPDATE, status: resolvedStatus, progress });

      if (resolvedStatus === "completed" || resolvedStatus === "cancelled") {
        yield put({ type: HEAL_POLL_STOP, status: resolvedStatus });
        return;
      }
    } catch {
      yield put({ type: HEAL_POLL_STOP, status: "error" });
      return;
    }

    yield delay(3000);
  }
}

export function* triggerBatchHealSaga(action: TriggerRequestAction) {
  try {
    const resp: AxiosResponse = yield call(triggerHealApi, action.filters);

    const batchId = resp.data.batch_id;
    yield put({ type: HEAL_TRIGGER_SUCCESS, batchId });

    const pollTask: Task = yield fork(pollLoop, batchId);

    const result: Action = yield take([HEAL_CANCEL_REQUEST, HEAL_POLL_STOP]);

    if (result.type === HEAL_CANCEL_REQUEST) {
      yield cancel(pollTask);
      try {
        yield call(cancelHealApi, batchId);
        yield put({ type: HEAL_CANCEL_SUCCESS });
      } catch (err) {
        const msg =
          (err as AxiosError)?.response?.data?.error ?? "Cancel failed";
        yield put({ type: HEAL_CANCEL_FAILURE, error: msg });
      }
    }
  } catch (err) {
    const axErr = err as AxiosError;
    const msg =
      (axErr?.response?.data as any)?.error ??
      axErr?.message ??
      "Failed to trigger batch heal";
    yield put({ type: HEAL_TRIGGER_FAILURE, error: msg });
  }
}

function checkActiveHealBatchApi() {
  return axios.get(`${baseUrl()}/store_manager/store-doctor/heal/active/`);
}

function* waitForAuth() {
  for (let i = 0; i < 20; i++) {
    const token: string | null = yield select(
      (s: RootState) => s.auth?.token ?? null
    );
    if (token) return true;
    yield delay(500);
  }
  return false;
}

function* reconnectBatchHealSaga() {
  try {
    const ready: boolean = yield call(waitForAuth);
    if (!ready) return;
    const resp: AxiosResponse = yield call(checkActiveHealBatchApi);
    const batchId = resp.data?.batch_id;
    if (!batchId) return;

    yield put({ type: HEAL_TRIGGER_SUCCESS, batchId });

    const pollTask: Task = yield fork(pollLoop, batchId);
    const result: Action = yield take([HEAL_CANCEL_REQUEST, HEAL_POLL_STOP]);

    if (result.type === HEAL_CANCEL_REQUEST) {
      yield cancel(pollTask);
      try {
        yield call(cancelHealApi, batchId);
        yield put({ type: HEAL_CANCEL_SUCCESS });
      } catch (err) {
        const msg =
          (err as AxiosError)?.response?.data?.error ?? "Cancel failed";
        yield put({ type: HEAL_CANCEL_FAILURE, error: msg });
      }
    }
  } catch {}
}

export function* batchHealSagaWatcher() {
  yield fork(reconnectBatchHealSaga);
  yield takeLatest(HEAL_TRIGGER_REQUEST, triggerBatchHealSaga);
}

export const requestBatchHealTrigger = (
  filters: Record<string, string | number>
) => ({
  type: HEAL_TRIGGER_REQUEST,
  filters,
});

export const requestBatchHealCancel = () => ({
  type: HEAL_CANCEL_REQUEST,
});

export const resetBatchHeal = () => ({
  type: HEAL_RESET,
});

export const getBatchHealStatus = (state: RootState) => state.batchHeal.status;

export const getBatchHealProgress = (state: RootState) =>
  state.batchHeal.progress;

export const getBatchHealError = (state: RootState) => state.batchHeal.error;
